import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { arrayPush } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';

import Section, {
  SectionHeader, SectionTitle, SectionBody, SectionBodyEmptyContent,
} from 'components/common/Section';
import IconCase from 'components/icons/IconCase';
import PrescriberCreateEditDialog from 'components/settings/PrescriberCreateEditDialog';
import PrescribersSearchDialog from 'components/settings/PrescribersSearchDialog';
import ReferralSourceCreateEditDialog from 'components/settings/ReferralSourceCreateEditDialog';
import SalesRepresentativeCreateEditDialog from 'components/settings/SalesRepresentativeCreateEditDialog';
import SortableList from 'components/common/SortableList';

import { changePatientResource } from 'redux/modules/patients';
import { addPrescriberResource } from 'redux/modules/prescribers';

import * as normalize from 'utils/normalize';
import formatPrescriberName from 'utils/formatPrescriberName';

import PrescribersItem from './PrescribersItem';
import PrescribersAddForm from './PrescribersAddForm';
import PrescribersLinkedDialog from './PrescribersLinkedDialog';


const styles = () => ({
  iconCase: {
    width: 20,
    height: 19,
  },
  form: {
    width: '65%',
  },
});

class Prescribers extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      searchDialogOpen: false,
      createDialogInitialValues: null,
      linkedDialogVariant: 'referralSource',
      linkedDialogOpen: false,
      linkedDialogInitialValues: {},
      referralSourceDialogOpen: false,
      salesDialogOpen: false,
      loading: false,
    };

    this.onCreateButtonClick = this.onCreateButtonClick.bind(this);
    this.onSearchDialogClose = this.onSearchDialogClose.bind(this);
    this.onPrescriberSelect = this.onPrescriberSelect.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onPrescriberSearchButtonClick = this.onPrescriberSearchButtonClick.bind(this);
    this.onLinkedDialogOpen = this.onLinkedDialogOpen.bind(this);
    this.onLinkedCreateButtonClick = this.onLinkedCreateButtonClick.bind(this);
    this.onReferralSourceDialogClose = this.onReferralSourceDialogClose.bind(this);
    this.onSalesDialogClose = this.onSalesDialogClose.bind(this);
    this.onLinkedDialogClose = this.onLinkedDialogClose.bind(this);
  }

  onCreateButtonClick() {
    this.setState({ searchDialogOpen: true });
  }

  onSearchDialogClose() {
    this.setState({
      searchDialogOpen: false,
      createDialogInitialValues: null,
    });
  }

  onPrescriberSelect(values) {
    const newState = {
      searchDialogOpen: false,
      createDialogOpen: true,
    };
    if (values) {
      const primaryAddress = values.addresses[0];
      const primaryTaxonomy = values.taxonomies[0];
      const phone = primaryAddress.phone.indexOf('+1 ') !== -1 ? primaryAddress.phone.replace('+1 ', '') : primaryAddress.phone;
      newState.createDialogInitialValues = {
        'First Name': values.first_name,
        'Last Name': values.last_name,
        NPI: values.npi.toString(),
        LIC: primaryTaxonomy.license,
        Phone: normalize.phone(phone),
        'Name Prefix': values.name_prefix ? values.name_prefix : '',
        'Name Suffix': (values.name_suffix && values.name_suffix !== '--') ? values.name_suffix : '',
        Credential: values.credential ? values.credential : '',
        address: {
          Street: primaryAddress.address,
          City: primaryAddress.city,
          State: primaryAddress.state,
          'Zip Code': primaryAddress.postal_code.slice(0, 5),
        },
        taxonomies: values.taxonomies.map(item => ({
          Code: item.code,
          Description: item.desc,
          'Is Primary': item.primary,
          ...item.state ? { State: item.state } : {},
          ...item.license ? { License: item.license } : {},
        })),
      };
    } else {
      newState.createDialogInitialValues = null;
    }
    this.setState(newState);
  }

  onCreateDialogClose() {
    this.setState({
      createDialogOpen: false,
      createDialogInitialValues: null,
    });
  }

  onPrescriberSearchButtonClick() {
    this.setState({
      createDialogOpen: false,
      searchDialogOpen: true,
    });
  }

  onLinkedDialogOpen(values) {
    if (values['Referral Source'] && values['Referral Source'].length > 0) {
      this.setState({
        linkedDialogOpen: true,
        linkedDialogVariant: 'referralSource',
        linkedDialogInitialValues: {
          'Referral Source': values['Referral Source'].map(item => ({ value: item.ID, label: item['Name of source'] })),
          'Prescriber': [ { value: values.ID, label: formatPrescriberName(values) } ],
        },
      });
    }
  }

  onLinkedDialogClose() {
    this.setState({
      linkedDialogOpen: false,
    });
  }

  onLinkedCreateButtonClick({ variant }) {
    this.setState({
      linkedDialogOpen: false,
      ...variant === 'referralSource' ? {
        referralSourceDialogOpen: true,
      } : {
        salesDialogOpen: true,
      },
    });
  }

  onReferralSourceDialogClose() {
    this.setState({
      referralSourceDialogOpen: false,
    });
  }

  onSalesDialogClose() {
    this.setState({
      salesDialogOpen: false,
    });
  }

  onChange(values) {
    const { patient, actions } = this.props;
    this.setState({ loading: true });
    actions.changePatientResource({
      'Record Number': patient['Record Number'],
      'Prescriber ID': values,
      resolve: () => {
        this.setState({ loading: false });
      },
      reject: () => {
        this.setState({ loading: false });
      },
    });
  }

  render() {
    const { patient, actions, classes } = this.props;
    const {
      searchDialogOpen,
      createDialogOpen,
      createDialogInitialValues,
      linkedDialogOpen,
      linkedDialogInitialValues,
      linkedDialogVariant,
      referralSourceDialogOpen,
      salesDialogOpen,
      loading,
    } = this.state;

    return (
      <Section>
        <SectionHeader>
          <SectionTitle>
            Prescribers
          </SectionTitle>
          <PrescribersAddForm
            className={classes.form}
            onCreateButtonClick={this.onCreateButtonClick}
            disabled={!patient}
            variant="default"
            onSubmitSuccess={(values) => {
              this.onChange([
                values.ID,
                ...patient.Prescriber.map(item => item.ID),
              ]);
              this.onLinkedDialogOpen(values);
            }}
            prescriber={patient.Prescriber}
          />
        </SectionHeader>
        <SectionBody padding="none">
          {(!patient || patient.Prescriber.length === 0) ? (
            <SectionBodyEmptyContent
              text="There are no prescribers yet"
              icon={(
                <IconCase className={classes.iconCase} />
              )}
            />
          ) : (
            <SortableList
              id="sortableListPrescriber"
              items={patient.Prescriber}
              itemComponent={PrescribersItem}
              dragDisabled={loading}
              onSortingChange={(values) => {
                this.onChange(values.sortedItems.map(item => item.ID));
              }}
            />
          )}
          <PrescriberCreateEditDialog
            form="prescriberCreate"
            open={createDialogOpen}
            onClose={this.onCreateDialogClose}
            onSubmitSuccess={(values) => {
              this.onChange([
                values.ID,
                ...patient.Prescriber.map(item => item.ID),
              ]);
              this.onLinkedDialogOpen(values);
            }}
            {...createDialogInitialValues ? {
              initialValues: createDialogInitialValues,
            } : {}}
            onSearchButtonClick={this.onPrescriberSearchButtonClick}
          />
          <PrescribersSearchDialog
            form="prescribersCreateRegistrySearch"
            open={searchDialogOpen}
            clearOnOpen={!createDialogInitialValues}
            onClose={this.onSearchDialogClose}
            onPrescriberSelect={this.onPrescriberSelect}
          />
          <PrescribersLinkedDialog
            {...patient}
            form="patientPrescribersLinked"
            variant={linkedDialogVariant}
            open={linkedDialogOpen}
            onClose={this.onLinkedDialogClose}
            onCreateButtonClick={this.onLinkedCreateButtonClick}
            initialValues={linkedDialogInitialValues}
            onSubmitSuccess={(values) => {
              if (linkedDialogVariant === 'referralSource') {
                const linkedSales = values['Referral Source'].reduce((accumulator, currentValue) => ([
                  ...accumulator,
                  ...currentValue['Sales Representative'].filter(item => accumulator
                    .filter(accumulatorItem => accumulatorItem.ID === item.ID).length === 0),
                ]), []);
                if (linkedSales.length > 0) {
                  this.setState({
                    linkedDialogVariant: 'sales',
                    linkedDialogInitialValues: {
                      ...this.state.linkedDialogInitialValues,
                      'Sales Representative': linkedSales.map(item => ({ value: item.ID, label: `${item['First Name']} ${item['Last Name']}` })),
                      'Referral Source': values['Referral Source'].map(item => ({ value: item.ID, label: item['Name of source'] })),
                    },
                  });
                } else {
                  this.onLinkedDialogClose();
                }
              } else {
                this.onLinkedDialogClose();
              }
            }}
          />
          <ReferralSourceCreateEditDialog
            form="referralSourceCreate"
            open={referralSourceDialogOpen}
            onClose={this.onReferralSourceDialogClose}
            initialValues={{
              'Prescriber': linkedDialogInitialValues.Prescriber,
            }}
            onSubmitSuccess={(values) => {
              this.setState({
                linkedDialogOpen: true,
                linkedDialogInitialValues: {
                  ...this.state.linkedDialogInitialValues,
                  'Referral Source': [
                    ...this.state.linkedDialogInitialValues['Referral Source'],
                    { value: values.ID, label: values['Name of source'] },
                  ],
                },
              });
            }}
          />
          <SalesRepresentativeCreateEditDialog
            form="salesRepresentativeCreate"
            open={salesDialogOpen}
            onClose={this.onSalesDialogClose}
            initialValues={{
              'Referral Source': linkedDialogInitialValues['Referral Source'],
            }}
            onSubmitSuccess={(values) => {
              this.setState({
                linkedDialogOpen: true,
                linkedDialogInitialValues: {
                    ...this.state.linkedDialogInitialValues,
                    'Sales Representative': [
                      ...this.state.linkedDialogInitialValues['Sales Representative'],
                      { value: values.ID, label: `${values['First Name']} ${values['Last Name']}` },
                    ],
                  },
              });
            }}
          />
        </SectionBody>
      </Section>
    );
  }
}

Prescribers.propTypes = {
  patient: PropTypes.shape({
    'Record Number': PropTypes.number,
    Prescriber: PropTypes.arrayOf(PropTypes.shape({
      ID: PropTypes.number.isRequired,
    })).isRequired,
  }),
  actions: PropTypes.shape({
    changePatientResource: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    iconCase: PropTypes.string.isRequired,
  }).isRequired,
};

Prescribers.defaultProps = {
  patient: null,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ changePatientResource, arrayPush }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  withStyles(styles)(Prescribers),
);



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/Prescribers/Prescribers.js